import { makeAutoObservable, runInAction } from "mobx";
import { FeedService, IFeedCategory, IFeedItem } from "./FeedService";

export class FeedStore {
  public Items: IFeedItem[] = [];
  public Categories: IFeedCategory[] = [];

  public IsLoading = false;
  public IsLoadingMore = false;
  public Error = "";

  public SelectedCategoryId: number | null = null;

  public NextCursor: string | null = null;
  public HasMore = false;
  public ItemsPerPageLimit = 20;

  private feedRequestId = 0;

  constructor() {
    makeAutoObservable(this);
  }

  public get IsAllCategoriesSelected(): boolean {
    return this.SelectedCategoryId == null;
  }

  public async selectCategory(categoryId: number | null): Promise<void> {
    if (this.SelectedCategoryId === categoryId) return;

    this.SelectedCategoryId = categoryId;
    this.NextCursor = null;
    this.HasMore = false;
    this.Items = [];
    this.Error = "";

    await this.loadFeed();
  }

  public async loadFeed(): Promise<void> {
    if (this.IsLoading) return;

    const requestId = ++this.feedRequestId;

    this.Error = "";
    this.IsLoading = true;
    this.IsLoadingMore = false;

    try {
      const [categories, feedResponse] = await Promise.all([FeedService.GetCategories(), FeedService.GetFeed(this.ItemsPerPageLimit, null, this.SelectedCategoryId)]);

      runInAction(() => {
        if (requestId !== this.feedRequestId) return;

        this.Categories = categories.filter((x) => x.isVisibleInFeed).sort((a, b) => a.sortOrder - b.sortOrder);

        this.Items = feedResponse.posts ?? [];
        this.NextCursor = feedResponse.nextCursor;
        this.HasMore = feedResponse.hasMore;
      });
    } catch {
      runInAction(() => {
        if (requestId !== this.feedRequestId) return;

        this.Error = "Не удалось загрузить ленту";
      });
    } finally {
      runInAction(() => {
        if (requestId !== this.feedRequestId) return;

        this.IsLoading = false;
      });
    }
  }

  public async loadMore(): Promise<void> {
    if (!this.HasMore || this.IsLoadingMore || this.IsLoading) return;

    const requestId = this.feedRequestId;
    const categoryId = this.SelectedCategoryId;
    const cursor = this.NextCursor;

    this.IsLoadingMore = true;

    try {
      const response = await FeedService.GetFeed(this.ItemsPerPageLimit, cursor, categoryId);

      runInAction(() => {
        if (requestId !== this.feedRequestId) return;
        if (categoryId !== this.SelectedCategoryId) return;

        this.Items = [...this.Items, ...(response.posts ?? [])];
        this.NextCursor = response.nextCursor;
        this.HasMore = response.hasMore;
      });
    } catch {
      runInAction(() => {
        if (requestId !== this.feedRequestId) return;

        this.Error = "Не удалось загрузить еще посты";
      });
    } finally {
      runInAction(() => {
        if (requestId !== this.feedRequestId) return;

        this.IsLoadingMore = false;
      });
    }
  }
}
